import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MButton } from "@/components/ui/atoms/MButtons";
import MSelectCard from "@/components/ui/atoms/MSelectCard";
import {
  IconMail,
  IconDeviceMobile,
  IconBrandGoogleFilled,
} from "@tabler/icons-react";
import { triggerNativeGoogleSignIn } from "@/utils/nativeBridge";
import { MExit } from "@/components/ui/atoms/MExit";
import { useRegisterContext } from "@/context/RegisterContext";

const SignUpOptions: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { credentials, updateCredentials } = useRegisterContext();

  // Initialize from context or default to null
  const selectedIdx = credentials.selectedOption ?? null;

  // Native Google signup trigger (WebView) or fallback to backend redirect
  const handleGoogleSignup = () => {
    if (
      typeof window !== "undefined" &&
      (window as any).ReactNativeWebView &&
      (window as any).ReactNativeWebView.postMessage
    ) {
      triggerNativeGoogleSignIn();
    } else {
      const apiBase = import.meta.env.VITE_API_URL || "/api";
      const url = `${apiBase.replace(/\/$/, "")}/auth/google/redirect`;
      window.location.href = url;
    }
  };

  const ctas = [
    {
      label: t("auth.optionCta.email"),
      icon: <IconMail className="w-5 h-5" />,
      onClick: () => navigate("/register/SignupForm/email"),
    },
    {
      label: t("auth.optionCta.mobile"),
      icon: <IconDeviceMobile className="w-5 h-5" />,
      onClick: () => navigate("/register/SignupForm/mobile"),
    }
  ];

  const handleSelectOption = (idx: number) => {
    updateCredentials({ selectedOption: idx });
    ctas[idx].onClick();
  };

  return (
    <div className="w-screen h-screen flex items-center justify-center bg-onboarding-base curvy-gradient-onboarding curvy-gradient-signup">
      <div className="auth-root relative overflow-hidden">
        {/* Back Button and Progress Bar */}
        <div className="onboarding-top-container">
          <MButton
            variant="back"
            size="md"
            onClick={() => navigate(-1)}
          />
          <MExit path="/" exitKey="exit" />
        </div>
        {/* Headings */}
        <div className="onboarding-main-container">
          <p className="auth-heading-1">{t("onboarding.step2.title")}</p>
          <p className="auth-subheading">{t("onboarding.step2.subtitle")}</p>
        </div>
        {/* Option Cards */}
        <div className="flex flex-col w-full gap-[16px] mt-[64px]">
          {ctas.map((cta, idx) => (
            <MSelectCard
              key={cta.label}
              text={cta.label}
              leftIcon={cta.icon}
              selected={selectedIdx === idx}
              onClick={() => handleSelectOption(idx)}
              variant="onboarding"
              className="w-full"
              tabIndex={0}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default SignUpOptions;
